import { COOKIE_FIRST_UTM, COOKIE_LAST_UTM, UTM_COOKIE_MAX_AGE_DAYS, UTM_SCHEMA_VERSION, UtmClassifyAction } from './enums';
import {
    classifySearchParams,
    cookiePayloadFromTouch,
    hasUtmSignal,
    isInternalNavigationTouch,
    isNullUtmSource,
    isSameUtmTouch,
    isTransactionalMedium,
    searchParamsFromLocation,
} from './classify-utm';
import type { ClassifiedUtmTouch, UtmData, UtmObject } from './types';

export function utmCookieDomain(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return undefined;
    if (host.includes('giglaya.com')) return '.giglaya.com';
    if (host.includes('sabbar.com')) return '.sabbar.com';
    return undefined;
}

function cookieBase(): string {
    const domain = utmCookieDomain();
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
    const domainPart = domain ? `; domain=${domain}` : '';
    const maxAge = UTM_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    return `; Max-Age=${maxAge}; path=/; SameSite=Lax${domainPart}${secure}`;
}

export function readUtmCookie(name: string): UtmObject | null {
    if (typeof document === 'undefined') return null;
    const prefix = `${name}=`;
    const parts = document.cookie.split(';');
    for (const part of parts) {
        const c = part.trim();
        if (!c.startsWith(prefix)) continue;
        const raw = c.substring(prefix.length);
        try {
            return JSON.parse(decodeURIComponent(raw)) as UtmObject;
        } catch {
            try {
                return JSON.parse(raw) as UtmObject;
            } catch {
                return null;
            }
        }
    }
    return null;
}

export function writeUtmCookie(name: string, value: UtmObject): void {
    if (typeof document === 'undefined') return;
    const json = JSON.stringify(value);
    document.cookie = `${name}=${encodeURIComponent(json)}${cookieBase()}`;
}

export function getUtmDataFromCookies(): UtmData | null {
    const firstUtm = readUtmCookie(COOKIE_FIRST_UTM);
    const lastUtm = readUtmCookie(COOKIE_LAST_UTM);
    if (!firstUtm && !lastUtm) return null;
    const isV2 = firstUtm?.schema_version === UTM_SCHEMA_VERSION || lastUtm?.schema_version === UTM_SCHEMA_VERSION;
    return {
        schema_version: isV2 ? UTM_SCHEMA_VERSION : undefined,
        firstUtm: firstUtm ?? undefined,
        lastUtm: lastUtm ?? undefined,
        isFirstUtmSameAsLastUtm: isSameUtmTouch(firstUtm, lastUtm),
    };
}

/** Classify the current URL (search and hash query) and persist first/last touch cookies. */
export function captureUtmFromCurrentLocation(): void {
    if (typeof window === 'undefined') return;
    try {
        const search = searchParamsFromLocation(window.location.search, window.location.hash);
        const result = classifySearchParams(search, document.referrer);
        if (result.action === UtmClassifyAction.Ignore || !result.touch) return;
        if (result.action === UtmClassifyAction.Reject) return;

        result.touch.landing_page = window.location.href;
        applyClassifiedTouchToCookies(result.touch);
    } catch {
        // Attribution must never break page load.
    }
}

export function applyClassifiedTouchToCookies(touch: ClassifiedUtmTouch): {
    writeFirst: boolean;
    writeLast: boolean;
    firstUtm: UtmObject | null;
    lastUtm: UtmObject | null;
} {
    const payload = cookiePayloadFromTouch(touch);
    const existingFirst = readUtmCookie(COOKIE_FIRST_UTM);
    const existingLast = readUtmCookie(COOKIE_LAST_UTM);

    const signal = hasUtmSignal(payload);

    // Internal navigation is the fallback. It is stored only when the cookie has no ad,
    // campaign, marketing email, or social visit, and a later one of those still replaces it.
    if (isInternalNavigationTouch(payload)) {
        if (hasUtmSignal(existingFirst) || hasUtmSignal(existingLast)) {
            return {
                writeFirst: false,
                writeLast: false,
                firstUtm: existingFirst,
                lastUtm: existingLast,
            };
        }
        const writeFirst = !existingFirst || !existingFirst.referrer_hostname;
        const writeLast = !existingLast || !existingLast.referrer_hostname;
        if (writeFirst) writeUtmCookie(COOKIE_FIRST_UTM, payload);
        if (writeLast) writeUtmCookie(COOKIE_LAST_UTM, payload);
        return {
            writeFirst,
            writeLast,
            firstUtm: writeFirst ? payload : existingFirst,
            lastUtm: writeLast ? payload : existingLast,
        };
    }

    // Seed the first touch on a brand new visitor, and upgrade a placeholder direct touch the
    // first time a real campaign shows up. Never let a later direct visit clobber it.
    const writeFirst = !existingFirst || (isNullUtmSource(existingFirst) && signal);
    if (writeFirst) writeUtmCookie(COOKIE_FIRST_UTM, payload);

    // isSameUtmTouch compares the canonical hash, which excludes landing_page and captured_at, so
    // simply navigating to another page never rewrites the cookie.
    const skipLast = isTransactionalMedium(touch.utm_medium)
        || Boolean(existingLast && !signal)
        || Boolean(existingLast && isSameUtmTouch(existingLast, payload));
    const writeLast = !skipLast;
    if (writeLast) writeUtmCookie(COOKIE_LAST_UTM, payload);

    return {
        writeFirst,
        writeLast,
        firstUtm: writeFirst ? payload : existingFirst,
        lastUtm: writeLast ? payload : existingLast,
    };
}
